import { Utils } from '@gv/triage-components';
import { createApi } from '@reduxjs/toolkit/query/react';
import { createSlice, createEntityAdapter } from '@reduxjs/toolkit';

import { URL } from 'api/constants';
import { apiQuery } from 'store/query';
import { TwilioPhoneNumber } from 'types';
import { generateUrl } from 'utils/helpers';
import { isLogoutAction } from 'store/slices/auth';
import {
  cacheByIdArg,
  providesList,
  cacheByIdArgProperty,
} from 'utils/query-cache';

import { sortWhiteboardConfig } from './helpers';
import {
  Hospital,
  RetellVoice,
  RetellConfig,
  KnowledgeBase,
  SetIVRRequest,
  POHospitalInfo,
  WhiteboardConfig,
  HospitalListData,
  GVTalkStatusData,
  SetRingGroupRequest,
  HospitalListRequest,
  UpdateStatusRequest,
  PhoneNumbersResponse,
  HospitalListResponse,
  HospitalBasicResponse,
  AdminHospitalListData,
  BuyPhoneNumberRequest,
  UnsubscribedEmailsData,
  POHospitalInfoResponse,
  HospitalDetailsResponse,
  GetGVTalkStatusResponse,
  GetKnowledgeBaseRequest,
  WhiteboardConfigResponse,
  SearchPhoneNumberRequest,
  AdminHospitalListRequest,
  ResubscribeEmailsRequest,
  SetGVTriageStatusPayload,
  GetKnowledgeBaseResponse,
  KnowledgeBaseQueryParams,
  UpdateGVTalkStatusRequest,
  AdminHospitalListResponse,
  UnsubscribedEmailsRequest,
  KnowledgeBasesWithMetadata,
  UnsubscribedEmailsResponse,
  HospitalBasicUpdatePayload,
  HospitalBasicCreatePayload,
  CreateKnowledgeBaseRequest,
  DeleteKnowledgeBaseRequest,
  AssignKnowledgeBasesRequest,
  CreateKnowledgeBaseResponse,
  TranscriptionStatusResponse,
  AssignKnowledgeBasesResponse,
  GetCallTranscriptionResponse,
  TriggerTranscriptionResponse,
  UpdateWhiteboardConfigRequest,
  AddKnowledgeBaseSourcesRequest,
  AddKnowledgeBaseSourcesResponse,
  BulkDeleteKnowledgeBasesRequest,
  DeleteKnowledgeBaseSourceRequest,
  BulkDeleteKnowledgeBasesResponse,
  DeleteKnowledgeBaseSourceResponse,
} from './types';

const {
  Helpers: { offset },
  Query: { addParamsToUrl },
} = Utils;

const hospitalsAdapter = createEntityAdapter<Hospital, number>({
  selectId: (item) => item.id,
});

const tagType = 'Hospitals';
const configTagType = 'WhiteboardConfig';
const emailsTagType = 'UnsubscribedEmails';
const knowledgeBaseTagType = 'KnowledgeBase';

export const hospitalsApi = createApi({
  baseQuery: apiQuery,
  reducerPath: 'hospitalsApi',
  tagTypes: [tagType, configTagType, emailsTagType, knowledgeBaseTagType],
  endpoints: (build) => ({
    sendWelcomeMail: build.mutation<void, number>({
      query: (id) => ({
        data: { id },
        method: 'post',
        url: URL.SEND_WELCOME_EMAIL,
      }),
    }),

    getRetellVoices: build.query<RetellVoice[], number>({
      query: (hospitalId) => ({
        method: 'get',
        url: generateUrl(URL.AI_AGENT_VOICES, { hospitalId }),
      }),
    }),

    delete: build.mutation<string, number>({
      invalidatesTags: cacheByIdArg(tagType),
      query: (id) => ({
        method: 'delete',
        url: generateUrl(URL.DELETE_HOSPITAL, { id }),
      }),
    }),

    getTranscriptionStatus: build.query<TranscriptionStatusResponse, string>({
      query: (callId) => ({
        method: 'get',
        url: generateUrl(URL.AI_AGENT_TRANSCRIPTION_STATUS, { callId }),
      }),
    }),

    // Retell AI Configuration endpoints
    getRetellConfig: build.query<RetellConfig, number>({
      providesTags: (result, error, id) => [{ id, type: tagType }],
      query: (hospitalId) => ({
        method: 'get',
        url: generateUrl(URL.AI_AGENT_CONFIG, { hospitalId }),
      }),
    }),

    create: build.mutation<Hospital, HospitalBasicCreatePayload>({
      transformResponse: (response: HospitalBasicResponse) =>
        response.data.Hospital,
      query: (data) => ({
        data,
        method: 'post',
        url: URL.ADD_HOSPITAL,
      }),
    }),

    basicInfo: build.query<POHospitalInfo, number>({
      transformResponse: (response: POHospitalInfoResponse) => response.data,
      query: (hospitalId) => ({
        method: 'get',
        url: generateUrl(URL.GET_BASIC_HOSPITAL_INFO, { hospitalId }),
      }),
    }),

    updateGVTalkStatus: build.mutation<void, UpdateGVTalkStatusRequest>({
      invalidatesTags: cacheByIdArgProperty(tagType),
      query: ({ id, status }) => ({
        method: 'post',
        data: { status },
        url: generateUrl(URL.HOSPITAL_GV_TALK_STATUS, { hospitalId: id }),
      }),
    }),

    details: build.query<Hospital, string>({
      providesTags: cacheByIdArg(tagType),
      transformResponse: (response: HospitalDetailsResponse) =>
        response.data.hospital,
      query: (id) => ({
        method: 'get',
        url: URL.GET_HOSPITAL_BASIC_INFO.replace('{id}', id),
      }),
    }),

    setGVTriageStatus: build.mutation<Hospital, SetGVTriageStatusPayload>({
      invalidatesTags: cacheByIdArgProperty(tagType),
      query: ({ id, ...data }) => ({
        method: 'post',
        url: URL.SET_GV_TRIAGE_STATUS,
        data: {
          hospital_id: id,
          ...data,
        },
      }),
    }),

    getGVTalkStatus: build.query<GVTalkStatusData, string>({
      providesTags: cacheByIdArg(tagType),
      transformResponse: (response: GetGVTalkStatusResponse) => response.data,
      query: (id) => ({
        method: 'get',
        url: generateUrl(URL.HOSPITAL_GV_TALK_STATUS, { hospitalId: id }),
      }),
    }),

    update: build.mutation<Hospital, HospitalBasicUpdatePayload>({
      invalidatesTags: cacheByIdArgProperty(tagType),
      transformResponse: (response: HospitalBasicResponse) =>
        response.data.Hospital,
      query: (data) => ({
        data,
        method: 'put',
        url: URL.ADD_HOSPITAL,
      }),
    }),

    getCallTranscription: build.query<GetCallTranscriptionResponse, string>({
      providesTags: (result, error, callId) => [
        { type: tagType, id: `transcription-${callId}` },
      ],
      query: (callId) => ({
        method: 'get',
        url: generateUrl(URL.AI_AGENT_CALL_TRANSCRIPTION, { callId }),
      }),
    }),

    triggerCaseTranscription: build.mutation<
      TriggerTranscriptionResponse,
      string
    >({
      invalidatesTags: (result, error, caseId) => [
        { type: tagType, id: `case-${caseId}` },
      ],
      query: (caseId) => ({
        method: 'post',
        url: generateUrl(URL.GVCOMM_CASE_TRANSCRIPTION_START, { caseId }),
      }),
    }),

    deleteKnowledgeBase: build.mutation<void, DeleteKnowledgeBaseRequest>({
      invalidatesTags: (result, error, { hospitalId }) => [
        { id: hospitalId, type: knowledgeBaseTagType },
      ],
      query: ({ kbId, hospitalId }) => ({
        method: 'delete',
        url: generateUrl(URL.AI_AGENT_KB_DELETE, { kbId, hospitalId }),
      }),
    }),

    updateChatAgent: build.mutation<
      { message: string; success: boolean },
      { hospitalId: number }
    >({
      invalidatesTags: (result, error, { hospitalId }) => [
        { type: tagType, id: hospitalId },
      ],
      query: ({ hospitalId }) => ({
        method: 'put',
        url: generateUrl(URL.AI_AGENT_CHAT_AGENT, { hospitalId }),
      }),
    }),

    triggerTranscription: build.mutation<TriggerTranscriptionResponse, number>({
      invalidatesTags: (result, error, transcriptionId) => [
        { type: tagType, id: `transcription-${transcriptionId}` },
      ],
      query: (transcriptionId) => ({
        method: 'post',
        url: generateUrl(URL.GVCOMM_TRANSCRIPTION_START, { transcriptionId }),
      }),
    }),

    getRetellCallDetails: build.query<
      any,
      { callId: string; hospitalId: number }
    >({
      providesTags: (result, error, { callId }) => [
        { type: tagType, id: `call-${callId}` },
      ],
      query: ({ callId, hospitalId }) => ({
        method: 'get',
        url: generateUrl(URL.AI_AGENT_CALL_DETAILS, { callId, hospitalId }),
      }),
    }),

    getWhiteboardConfig: build.query<WhiteboardConfig, number>({
      providesTags: [configTagType],
      transformResponse: (response: WhiteboardConfigResponse) =>
        sortWhiteboardConfig(response.data),
      query: (id) => ({
        method: 'get',
        url: generateUrl(URL.GET_HOSPITAL_WHITEBOARD_CONFIG, {
          hospitalId: id,
        }),
      }),
    }),

    deleteChatAgent: build.mutation<
      { message: string; success: boolean },
      { hospitalId: number }
    >({
      invalidatesTags: (result, error, { hospitalId }) => [
        { type: tagType, id: hospitalId },
      ],
      query: ({ hospitalId }) => ({
        method: 'delete',
        url: generateUrl(URL.AI_AGENT_CHAT_AGENT, { hospitalId }),
      }),
    }),

    purchasePhoneNumber: build.mutation<void, BuyPhoneNumberRequest>({
      invalidatesTags: cacheByIdArgProperty(tagType),
      query: ({ id, hospital, ...props }) => ({
        method: 'post',
        url: hospital ? URL.PURCHASE_NUMBER : URL.PURCHASE_NUMBER_VENDOR,
        data: {
          hospital,
          hospital_id: id,
          ...props,
        },
      }),
    }),

    unsubscribedEmails: build.query<
      UnsubscribedEmailsData,
      UnsubscribedEmailsRequest
    >({
      providesTags: [emailsTagType],
      transformResponse: (response: UnsubscribedEmailsResponse) =>
        response.data,
      query: ({ hospitalId }) => ({
        method: 'get',
        url: generateUrl(URL.HOSPITAL_UNSUBSCRIBED_EMAILS, { hospitalId }),
      }),
    }),

    resubscribeEmails: build.mutation<void, ResubscribeEmailsRequest>({
      invalidatesTags: [emailsTagType],
      query: ({ emails, hospitalId }) => {
        const url = generateUrl(URL.HOSPITAL_RESUBSCRIBE_EMAILS, {
          hospitalId,
        });

        return {
          method: 'post',
          url: addParamsToUrl(url, { emails: emails.join(',') }),
        };
      },
    }),

    createChatAgent: build.mutation<
      { message: string; success: boolean; chat_agent_id: string },
      { hospitalId: number }
    >({
      invalidatesTags: (result, error, { hospitalId }) => [
        { type: tagType, id: hospitalId },
      ],
      query: ({ hospitalId }) => ({
        method: 'post',
        url: generateUrl(URL.AI_AGENT_CHAT_AGENT, { hospitalId }),
      }),
    }),

    getRetellChatDetails: build.query<
      any,
      { chatId: string; hospitalId: number }
    >({
      providesTags: (result, error, { chatId, hospitalId }) => [
        { type: tagType, id: `chat-${hospitalId}-${chatId}` },
      ],
      query: ({ chatId, hospitalId }) => ({
        method: 'get',
        url: generateUrl(URL.AI_AGENT_CHAT_DETAILS, { chatId, hospitalId }),
      }),
    }),

    updateWhiteboardConfig: build.mutation<
      WhiteboardConfig,
      UpdateWhiteboardConfigRequest
    >({
      invalidatesTags: [configTagType],
      transformResponse: (response: WhiteboardConfigResponse) => response.data,
      query: ({ hospitalId, ...data }) => ({
        data,
        method: 'patch',
        url: generateUrl(URL.GET_HOSPITAL_WHITEBOARD_CONFIG, { hospitalId }),
      }),
    }),

    endRetellChat: build.mutation<
      { message: string },
      { chatId: string; hospitalId: number }
    >({
      invalidatesTags: (result, error, { hospitalId }) => [
        { type: tagType, id: `chats-${hospitalId}` },
      ],
      query: ({ chatId, hospitalId }) => ({
        method: 'post',
        data: { chat_id: chatId },
        url: generateUrl(URL.AI_AGENT_END_CHAT, { hospitalId }),
      }),
    }),

    updateStatus: build.mutation<Hospital, UpdateStatusRequest>({
      invalidatesTags: cacheByIdArgProperty(tagType),
      transformResponse: (response: HospitalDetailsResponse) =>
        response.data.hospital,
      query: ({ id, status, ...rest }) => ({
        method: 'put',
        url: URL.UPDATE_HOSPITAL_STATUS,
        data: {
          status,
          hospitalId: id,
          ...rest,
        },
      }),
    }),

    updateRetellConfig: build.mutation<
      { message: string; success: boolean; agent_id: string },
      { hospitalId: number; config: RetellConfig }
    >({
      invalidatesTags: (result, error, { hospitalId }) => [
        { type: tagType, id: hospitalId },
      ],
      query: ({ config, hospitalId }) => ({
        data: config,
        method: 'put',
        url: generateUrl(URL.AI_AGENT_CONFIG, { hospitalId }),
      }),
    }),

    setIVR: build.mutation<void, SetIVRRequest>({
      invalidatesTags: cacheByIdArgProperty(tagType),
      query: ({ id, isNew, ivrId, oldIvrId }) => ({
        method: isNew ? 'post' : 'put',
        url: isNew ? URL.ADD_INITIAL_IVR : URL.UPDATE_INITIAL_IVR,
        data: {
          hospital_id: id,
          ...(isNew
            ? { ivr_master_id: ivrId }
            : { newIvr_id: ivrId, oldIvr_id: oldIvrId }),
        },
      }),
    }),

    phoneNumbers: build.query<TwilioPhoneNumber[], SearchPhoneNumberRequest>({
      transformResponse: (response: PhoneNumbersResponse) =>
        response.data.numbers,
      query: ({ hospital, ...props }) => {
        const data: Record<string, any> = {
          hospital,
          ...props,
        };
        return {
          data,
          method: 'post',
          url: hospital ? URL.SEARCH_NUMBER : URL.SEARCH_NUMBER_VENDOR,
        };
      },
    }),

    getKnowledgeBase: build.query<KnowledgeBase, GetKnowledgeBaseRequest>({
      transformResponse: (response: GetKnowledgeBaseResponse) =>
        response.knowledge_base,
      providesTags: (result, error, { kbId, hospitalId }) => [
        { type: knowledgeBaseTagType, id: `${hospitalId}-${kbId}` },
      ],
      query: ({ kbId, hospitalId }) => ({
        method: 'get',
        url: generateUrl(URL.AI_AGENT_KB_DETAILS, { kbId, hospitalId }),
      }),
    }),

    deleteKnowledgeBaseSource: build.mutation<
      DeleteKnowledgeBaseSourceResponse,
      DeleteKnowledgeBaseSourceRequest
    >({
      invalidatesTags: (result, error, { kbId, hospitalId }) => [
        { type: knowledgeBaseTagType, id: `${hospitalId}-${kbId}` },
      ],
      query: ({ kbId, sourceId, hospitalId }) => ({
        method: 'delete',
        url: generateUrl(URL.AI_AGENT_KB_DELETE_SOURCE, {
          kbId,
          sourceId,
          hospitalId,
        }),
      }),
    }),

    bulkDeleteKnowledgeBases: build.mutation<
      BulkDeleteKnowledgeBasesResponse,
      BulkDeleteKnowledgeBasesRequest
    >({
      invalidatesTags: (result, error, { hospitalId }) => [
        { id: hospitalId, type: knowledgeBaseTagType },
      ],
      query: ({ hospitalId, knowledgeBaseIds }) => ({
        method: 'post',
        data: { knowledge_base_ids: knowledgeBaseIds },
        url:
          generateUrl(URL.AI_AGENT_KNOWLEDGE_BASES, { hospitalId }) +
          '/bulk-delete',
      }),
    }),

    updateEnabledKnowledgeBases: build.mutation<
      AssignKnowledgeBasesResponse,
      AssignKnowledgeBasesRequest
    >({
      invalidatesTags: (result, error, { hospitalId }) => [
        { type: tagType, id: hospitalId },
        { id: hospitalId, type: knowledgeBaseTagType },
      ],
      query: ({ hospitalId, knowledgeBaseIds }) => ({
        method: 'post',
        data: { knowledge_base_ids: knowledgeBaseIds },
        url: generateUrl(URL.AI_AGENT_KB_UPDATE_ENABLED, { hospitalId }),
      }),
    }),

    setRingGroup: build.mutation<void, SetRingGroupRequest>({
      invalidatesTags: cacheByIdArgProperty(tagType),
      query: ({ id, isNew, ringGroupId, oldRingGroupId }) => ({
        method: isNew ? 'post' : 'put',
        url: isNew ? URL.ADD_INITIAL_RING : URL.UPDATE_INITIAL_RING,
        data: {
          hospital_id: id,
          ...(isNew
            ? { ring_group_id: ringGroupId }
            : {
                newRingGroup_id: ringGroupId,
                oldRingGroup_id: oldRingGroupId,
              }),
        },
      }),
    }),

    createKnowledgeBase: build.mutation<
      CreateKnowledgeBaseResponse,
      CreateKnowledgeBaseRequest
    >({
      invalidatesTags: (result, error, { hospitalId }) => [
        { id: hospitalId, type: knowledgeBaseTagType },
      ],
      query: ({ file, name, hospitalId }) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('name', name);
        return {
          method: 'post',
          data: formData,
          url: generateUrl(URL.AI_AGENT_KNOWLEDGE_BASE, { hospitalId }),
        };
      },
    }),

    list: build.query<HospitalListData, HospitalListRequest>({
      providesTags: (result) => providesList(tagType)(result?.hospitals),
      transformResponse: (response: HospitalListResponse) => response.data,
      query: ({
        page,
        limit = 20,
        searchKeyword,
        hospital = true,
        ...props
      }) => {
        const data: Record<string, any> = {
          limit,
          hospital,
          searchKeyword,
          offset: offset(page!, limit),
          ...props,
        };
        return {
          data,
          method: 'post',
          url: searchKeyword ? URL.SEARCH_HOSPITAL : URL.GET_HOSPITAL_ALL,
        };
      },
    }),

    getRetellChats: build.query<
      { chats: any[]; total: number },
      {
        skip?: number;
        limit?: number;
        endDate?: string;
        hospitalId: number;
        startDate?: string;
      }
    >({
      providesTags: (result, error, { hospitalId }) => [
        { type: tagType, id: `chats-${hospitalId}` },
      ],
      query: ({ endDate, skip = 0, startDate, hospitalId, limit = 20 }) => {
        const params: any = { skip, limit };
        if (startDate) {
          params.startDate = startDate;
        }
        if (endDate) {
          params.endDate = endDate;
        }

        const baseUrl = generateUrl(URL.AI_AGENT_CHATS, { hospitalId });
        const url = addParamsToUrl(baseUrl, params);

        return {
          url,
          method: 'get',
        };
      },
    }),

    getRetellCalls: build.query<
      { calls: any[]; total: number },
      {
        skip?: number;
        limit?: number;
        endDate?: string;
        hospitalId: number;
        startDate?: string;
      }
    >({
      providesTags: (result, error, { hospitalId }) => [
        { type: tagType, id: `calls-${hospitalId}` },
      ],
      query: ({ endDate, skip = 0, startDate, hospitalId, limit = 20 }) => {
        // Build params object, only including defined values
        const params: any = { skip, limit };
        if (startDate) {
          params.startDate = startDate;
        }
        if (endDate) {
          params.endDate = endDate;
        }

        const baseUrl = generateUrl(URL.AI_AGENT_CALLS, { hospitalId });
        const url = addParamsToUrl(baseUrl, params);

        return {
          url,
          method: 'get',
        };
      },
    }),

    addKnowledgeBaseSources: build.mutation<
      AddKnowledgeBaseSourcesResponse,
      AddKnowledgeBaseSourcesRequest
    >({
      invalidatesTags: (result, error, { kbId, hospitalId }) => [
        { id: hospitalId, type: knowledgeBaseTagType },
        { type: knowledgeBaseTagType, id: `${hospitalId}-${kbId}` },
      ],
      query: ({ kbId, file, urls, texts, hospitalId }) => {
        const formData = new FormData();
        if (file) {
          formData.append('file', file);
        }
        if (texts && texts.length > 0) {
          formData.append('texts', JSON.stringify(texts));
        }
        if (urls && urls.length > 0) {
          formData.append('urls', JSON.stringify(urls));
        }
        return {
          method: 'put',
          data: formData,
          url: generateUrl(URL.AI_AGENT_KB_ADD_SOURCES, { kbId, hospitalId }),
        };
      },
    }),

    adminList: build.query<AdminHospitalListData, AdminHospitalListRequest>({
      providesTags: (result) => providesList(tagType)(result?.rows),
      transformResponse: (response: AdminHospitalListResponse) => response.data,
      query: ({
        page,
        type,
        state,
        search,
        status,
        services,
        speciality,
        limit = 20,
        protocolLevel,
      }) => {
        const url = addParamsToUrl(URL.GET_ADMIN_HOSPITAL_LIST, {
          limit,
          search,
          status,
          offset: offset(page, limit),
          type: type.length === 0 ? null : type,
          state: state.length === 0 ? null : state,
          speciality: speciality.length === 0 ? null : speciality,
          protocol_level: protocolLevel.length === 0 ? null : protocolLevel,
          filters: services.length === 0 ? null : JSON.stringify({ services }),
        });

        return {
          url,
          method: 'get',
        };
      },
    }),

    getKnowledgeBases: build.query<
      KnowledgeBasesWithMetadata,
      KnowledgeBaseQueryParams
    >({
      providesTags: (result, error, params) => [
        { id: params.hospitalId, type: knowledgeBaseTagType },
      ],
      query: ({ page, limit, search, hospitalId }) => {
        const params = new URLSearchParams();
        if (page) {
          params.append('page', page.toString());
        }
        if (limit) {
          params.append('limit', limit.toString());
        }
        if (search) {
          params.append('search', search);
        }

        const queryString = params.toString();
        const url = generateUrl(URL.AI_AGENT_KNOWLEDGE_BASES, { hospitalId });

        return {
          method: 'get',
          url: queryString ? `${url}?${queryString}` : url,
        };
      },
      transformResponse: (response: any) => {
        if (response.knowledge_bases) {
          return {
            page: response.page || 1,
            total: response.total || 0,
            limit: response.limit || 20,
            totalPages: response.totalPages || 1,
            knowledge_bases: response.knowledge_bases,
            assigned_ids: response.assigned_ids || [],
            protected_ids: response.protected_ids || [],
          };
        }
        return {
          page: 1,
          total: 0,
          limit: 20,
          totalPages: 0,
          assigned_ids: [],
          protected_ids: [],
          knowledge_bases: [],
        };
      },
    }),
  }),
});

export const hospitalsSlice = createSlice({
  reducers: {},
  name: 'hospitals',
  initialState: hospitalsAdapter.getInitialState(),
  extraReducers: (builder) => {
    builder.addMatcher(
      hospitalsApi.endpoints.list.matchFulfilled,
      (state, { payload }) => {
        hospitalsAdapter.upsertMany(state, payload.hospitals);
      }
    );

    builder.addMatcher(
      hospitalsApi.endpoints.details.matchFulfilled,
      (state, { payload }) => {
        hospitalsAdapter.upsertOne(state, payload);
      }
    );

    builder.addMatcher(
      hospitalsApi.endpoints.update.matchFulfilled,
      (state, { payload }) => {
        hospitalsAdapter.updateOne(state, {
          changes: payload,
          id: hospitalsAdapter.selectId(payload),
        });
      }
    );

    builder.addMatcher(
      hospitalsApi.endpoints.delete.matchFulfilled,
      (state, { meta }) => {
        hospitalsAdapter.removeOne(state, meta.arg.originalArgs);
      }
    );

    builder.addMatcher(isLogoutAction, (state) => {
      hospitalsAdapter.removeAll(state);
    });
  },
});

export const {
  useListQuery,
  useDetailsQuery,
  useAdminListQuery,
  useUpdateMutation,
  useDeleteMutation,
  useCreateMutation,
  useSetIVRMutation,
  useBasicInfoQuery,
  useGetRetellCallsQuery,
  useGetRetellChatsQuery,
  useGetGVTalkStatusQuery,
  useUpdateStatusMutation,
  useSetRingGroupMutation,
  useGetRetellConfigQuery,
  useGetRetellVoicesQuery,
  useEndRetellChatMutation,
  useGetKnowledgeBaseQuery,
  useLazyPhoneNumbersQuery,
  useGetKnowledgeBasesQuery,
  useUnsubscribedEmailsQuery,
  useSendWelcomeMailMutation,
  useCreateChatAgentMutation,
  useUpdateChatAgentMutation,
  useDeleteChatAgentMutation,
  useGetWhiteboardConfigQuery,
  useGetRetellChatDetailsQuery,
  useResubscribeEmailsMutation,
  useSetGVTriageStatusMutation,
  useGetCallTranscriptionQuery,
  useUpdateGVTalkStatusMutation,
  useUpdateRetellConfigMutation,
  useDeleteKnowledgeBaseMutation,
  useCreateKnowledgeBaseMutation,
  usePurchasePhoneNumberMutation,
  useGetTranscriptionStatusQuery,
  useTriggerTranscriptionMutation,
  useUpdateWhiteboardConfigMutation,
  useAddKnowledgeBaseSourcesMutation,
  useTriggerCaseTranscriptionMutation,
  useDeleteKnowledgeBaseSourceMutation,
  useUpdateEnabledKnowledgeBasesMutation,
} = hospitalsApi;

export { tagType as hospitalTagType };
